import { useEffect, useMemo, useRef, useState } from "react";
import { Radio, Volume2, VolumeX, Flame, CloudRain, CarFront, GripVertical, MapPin } from "lucide-react";
import { useSdkStore } from "./sdkStore";
import { buildSimulation, posAt } from "./simulation";
import { dispatchSpeak, setRadioEnabled } from "./radio";

const PRIO_CLS = { P1: "text-red-400 border-red-400/50 bg-red-500/10", P2: "text-nc-orange border-nc-orange/50 bg-nc-orange/10", P3: "text-zinc-300 border-white/20" };
const STATUS_CLS = { pending: "text-zinc-400", dispatched: "text-nc-blue", on_scene: "text-nc-orange", resolved: "text-nc-green" };

const BASE_UNITS = [
  { id: "adam-1", callsign: "ADAM-1", type: "Patrol" },
  { id: "adam-2", callsign: "ADAM-2", type: "Patrol" },
  { id: "lincoln-1", callsign: "LINCOLN-1", type: "Supervisor" },
];

function incidentSet(scenarioId) {
  if (scenarioId === "barricade") {
    return [
      { id: "I-2001", t: 4, prio: "P2", title: "911 — Domestic disturbance, possible hostage", loc: "14 Reseda Ave", x: 0, z: 0, resolveAt: 120 },
      { id: "I-2002", t: 40, prio: "P1", title: "Negotiation in progress — shots possible", loc: "14 Reseda Ave", x: 0, z: 0, resolveAt: 140 },
    ];
  }
  return [
    { id: "I-1001", t: 4, prio: "P1", title: "911 — Shots reported, multiple callers", loc: "Northgate Mall", x: 0, z: 0, resolveAt: 95 },
    { id: "I-1002", t: 15, prio: "P1", title: "Active shooter confirmed — crowd fleeing", loc: "Northgate Mall — South Exit", x: 0, z: 28, resolveAt: 120 },
  ];
}

const HEATMAPS = {
  traffic: { label: "Traffic", icon: CarFront, color: "rgba(46,107,255,0.35)", blobs: [[-60, 60, 30], [50, -30, 26], [10, 70, 22]] },
  weather: { label: "Weather", icon: CloudRain, color: "rgba(167,139,250,0.3)", blobs: [[-30, -50, 45], [60, 50, 34]] },
  crime: { label: "Crime Risk", icon: Flame, color: "rgba(239,68,68,0.32)", blobs: [[0, 0, 30], [-70, -40, 20], [70, 45, 18]] },
};

export default function CadTab() {
  const nodes = useSdkStore((s) => s.nodes);
  const edges = useSdkStore((s) => s.edges);
  const mission = useSdkStore((s) => s.mission);
  const [scenarioId, setScenarioId] = useState(mission.scenario === "barricade" ? "barricade" : "active_shooter");
  const [assignments, setAssignments] = useState({}); // incidentId -> [unitId]
  const [radioOn, setRadioOn] = useState(false);
  const [heat, setHeat] = useState({ traffic: false, weather: false, crime: false });
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const spokenRef = useRef(new Set());
  const radioLogRef = useRef([]);

  const sim = useMemo(() => buildSimulation(scenarioId, nodes, edges), [scenarioId, nodes, edges]);
  const incidents = useMemo(() => incidentSet(scenarioId), [scenarioId]);
  const radioEvents = useMemo(() => sim.events.filter((e) => e.kind === "radio"), [sim]);

  useEffect(() => {
    if (!playing) return undefined;
    const iv = setInterval(() => setT((x) => Math.min(x + 0.5, 150)), 500);
    return () => clearInterval(iv);
  }, [playing]);

  // voice dispatch on newly-active incidents + blueprint radio broadcasts
  useEffect(() => {
    if (!radioOn) return;
    incidents.forEach((inc) => {
      if (t >= inc.t && !spokenRef.current.has(inc.id)) {
        spokenRef.current.add(inc.id);
        dispatchSpeak(`All units, ${inc.prio === "P1" ? "Priority 1" : "Priority 2"}: ${inc.title} at ${inc.loc}.`);
        radioLogRef.current.push({ t: inc.t, msg: `DISPATCH // ${inc.title} @ ${inc.loc}` });
      }
    });
    radioEvents.forEach((ev, i) => {
      const key = `bp-${i}`;
      if (t >= ev.t && !spokenRef.current.has(key)) {
        spokenRef.current.add(key);
        dispatchSpeak(ev.label.replace("RADIO // ", ""));
        radioLogRef.current.push({ t: ev.t, msg: ev.label });
      }
    });
  }, [t, radioOn, incidents, radioEvents]);

  const toggleRadio = () => {
    const next = !radioOn;
    setRadioOn(next);
    setRadioEnabled(next);
    if (!next && "speechSynthesis" in window) window.speechSynthesis.cancel();
  };

  const unitStatus = (unitId) => {
    const incId = Object.keys(assignments).find((k) => assignments[k].includes(unitId));
    if (!incId) return "10-8 // Available";
    const inc = incidents.find((i) => i.id === incId);
    if (!inc) return "10-8 // Available";
    if (t >= inc.resolveAt) return "10-8 // Clear";
    if (t >= inc.t + 18) return "On scene";
    return "En route";
  };

  const incStatus = (inc) => {
    const units = assignments[inc.id] || [];
    if (t >= inc.resolveAt && units.length) return "resolved";
    if (t < inc.t) return "pending";
    if (!units.length) return "pending";
    return t >= inc.t + 18 ? "on_scene" : "dispatched";
  };

  const onDrop = (e, incId) => {
    e.preventDefault();
    const unitId = e.dataTransfer.getData("text/unit");
    if (!unitId) return;
    setAssignments((a) => {
      const cleaned = Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v.filter((u) => u !== unitId)]));
      cleaned[incId] = [...(cleaned[incId] || []), unitId];
      return cleaned;
    });
    if (radioOn) {
      const u = BASE_UNITS.find((x) => x.id === unitId);
      dispatchSpeak(`${u?.callsign || unitId}, 10-4, responding.`);
    }
  };

  // map: world coords (+-120) -> svg viewbox 0..400
  const mx = (x) => 200 + x * 1.55;
  const mz = (z) => 200 + z * 1.55;
  const visibleEntities = sim.entities.filter((e) => ["police", "swat", "suspect", "player", "hostage", "vehicle"].includes(e.kind));

  return (
    <div data-testid="cad-tab" className="flex flex-col gap-4">
      {/* console header */}
      <div className="border border-white/10 bg-[#070707] p-4 flex flex-wrap items-center gap-3">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Radio className="w-4 h-4" /> CAD Console // Sector 4 Dispatch</p>
        <select data-testid="cad-scenario" value={scenarioId} onChange={(e) => { setScenarioId(e.target.value); setT(0); setAssignments({}); spokenRef.current = new Set(); radioLogRef.current = []; }} className="bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[11px] text-zinc-100 focus:outline-none ml-2">
          <option value="active_shooter">Active Shooter — Northgate Mall</option>
          <option value="barricade">High-Risk Barricade — Reseda Ave</option>
        </select>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {Object.entries(HEATMAPS).map(([k, h]) => (
            <button key={k} data-testid={`cad-heat-${k}`} onClick={() => setHeat((s) => ({ ...s, [k]: !s[k] }))} className={`font-mono text-[8px] tracking-[0.15em] uppercase px-2.5 py-2 border transition-colors flex items-center gap-1 ${heat[k] ? "border-nc-orange text-nc-orange" : "border-white/10 text-zinc-500 hover:text-zinc-200"}`}>
              <h.icon className="w-3 h-3" /> {h.label}
            </button>
          ))}
          <button data-testid="cad-radio-toggle" onClick={toggleRadio} className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${radioOn ? "border-nc-green text-nc-green bg-nc-green/10" : "border-white/15 text-zinc-400"}`}>
            {radioOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} Radio {radioOn ? "Live" : "Muted"}
          </button>
          <button data-testid="cad-play-toggle" onClick={() => setPlaying((p) => !p)} className="font-mono text-[9px] uppercase border border-white/15 text-zinc-300 px-3 py-2">{playing ? "Hold" : "Resume"}</button>
          <span data-testid="cad-clock" className="font-mono text-[11px] text-nc-blue">T+{t.toFixed(0)}s</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-4">
        {/* left: incidents + units */}
        <div className="flex flex-col gap-4">
          <div className="border border-white/10 bg-[#070707]">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase px-4 py-3 border-b border-white/10">Active Incidents</p>
            <div data-testid="cad-incidents" className="flex flex-col">
              {incidents.map((inc, i) => {
                const active = t >= inc.t;
                const status = incStatus(inc);
                const units = assignments[inc.id] || [];
                return (
                  <div
                    key={inc.id}
                    data-testid={`cad-incident-${i}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, inc.id)}
                    className={`px-4 py-3 border-b border-white/5 transition-colors ${active ? "" : "opacity-35"} ${status === "resolved" ? "bg-nc-green/5" : ""}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-[10px] text-zinc-500 w-14">{inc.id}</span>
                      <span className={`font-mono text-[8px] tracking-[0.15em] border px-1.5 py-0.5 ${PRIO_CLS[inc.prio]}`}>{inc.prio}</span>
                      <span className="font-mono text-[11px] text-zinc-100 flex-1">{inc.title}</span>
                      <span className={`font-mono text-[8px] uppercase tracking-[0.15em] ${STATUS_CLS[status]}`}>{status.replace("_", " ")}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap pl-14">
                      <span className="font-mono text-[8px] text-zinc-600 flex items-center gap-1"><MapPin className="w-3 h-3" /> {inc.loc} · T+{inc.t}s</span>
                      {units.length === 0 ? (
                        <span className="font-mono text-[8px] text-zinc-600 border border-dashed border-white/15 px-2 py-1">Drag a unit here to assign</span>
                      ) : (
                        units.map((u) => (
                          <span key={u} className="font-mono text-[8px] text-nc-blue border border-nc-blue/40 bg-nc-blue/10 px-2 py-1">
                            {BASE_UNITS.find((x) => x.id === u)?.callsign || u}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-white/10 bg-[#070707]">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase px-4 py-3 border-b border-white/10">Unit Roster — drag to assign</p>
            <div data-testid="cad-units" className="p-3 flex flex-col gap-1.5">
              {BASE_UNITS.map((u, i) => (
                <div key={u.id} data-testid={`cad-unit-${i}`} draggable onDragStart={(e) => e.dataTransfer.setData("text/unit", u.id)} className="flex items-center gap-3 border border-white/10 bg-black/40 px-3 py-2.5 cursor-grab active:cursor-grabbing hover:border-nc-blue/40 transition-colors">
                  <GripVertical className="w-3.5 h-3.5 text-zinc-600" />
                  <span className="font-mono text-[10px] text-zinc-100 w-24">{u.callsign}</span>
                  <span className="font-mono text-[8px] text-zinc-500 uppercase flex-1">{u.type}</span>
                  <span className={`font-mono text-[8px] uppercase tracking-[0.15em] ${unitStatus(u.id).includes("Available") || unitStatus(u.id).includes("Clear") ? "text-nc-green" : "text-nc-orange"}`}>{unitStatus(u.id)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* right: 2D tactical map */}
        <div className="border border-white/10 bg-[#070707] flex flex-col">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase px-4 py-3 border-b border-white/10">Tactical Map // synced to sandbox coordinates</p>
          <div data-testid="cad-map" className="flex-1 min-h-[420px]">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              <rect width="400" height="400" fill="#060608" />
              {Array.from({ length: 20 }).map((_, i) => (
                <g key={i} stroke="#141418" strokeWidth="0.5">
                  <line x1={i * 20} y1="0" x2={i * 20} y2="400" />
                  <line x1="0" y1={i * 20} x2="400" y2={i * 20} />
                </g>
              ))}
              {/* heatmap blobs */}
              {Object.entries(HEATMAPS).map(([k, h]) => heat[k] && h.blobs.map(([x, z, r], bi) => (
                <circle key={`${k}-${bi}`} data-testid={bi === 0 ? `cad-heatblob-${k}` : undefined} cx={mx(x)} cy={mz(z)} r={r * 1.55} fill={h.color} style={{ filter: "blur(10px)" }} />
              )))}
              {sim.buildings.map((b, i) => (
                <rect key={i} x={mx(b.x - b.w / 2)} y={mz(b.z - b.d / 2)} width={b.w * 1.55} height={b.d * 1.55} fill="#15151a" stroke="#2E6BFF" strokeOpacity="0.25" strokeWidth="0.7" />
              ))}
              {sim.zones.map((z) => (
                <g key={z.id}>
                  <circle cx={mx(z.x)} cy={mz(z.z)} r={z.r * 1.55} fill={z.color} fillOpacity="0.05" stroke={z.color} strokeWidth="1" strokeDasharray="4 3" />
                  <text x={mx(z.x)} y={mz(z.z) - z.r * 1.55 - 3} fill={z.color} fontSize="7" fontFamily="monospace" textAnchor="middle">{z.label.toUpperCase()}</text>
                </g>
              ))}
              {incidents.filter((i) => t >= i.t).map((inc) => (
                <g key={inc.id}>
                  <circle cx={mx(inc.x)} cy={mz(inc.z)} r="7" fill="none" stroke="#ef4444" strokeWidth="1.4">
                    <animate attributeName="r" values="5;11;5" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={mx(inc.x)} cy={mz(inc.z)} r="2.5" fill="#ef4444" />
                </g>
              ))}
              {visibleEntities.map((e) => {
                const [x, z] = posAt(e.track, t);
                if (e.spawnAt && t < e.spawnAt) return null;
                const col = { police: "#2E6BFF", swat: "#67e8f9", suspect: "#ef4444", player: "#22c55e", hostage: "#FF6B1A", vehicle: "#71717a" }[e.kind];
                return e.kind === "vehicle"
                  ? <rect key={e.id} x={mx(x) - 3.5} y={mz(z) - 2} width="7" height="4" fill={col} />
                  : <circle key={e.id} cx={mx(x)} cy={mz(z)} r="2.6" fill={col} />;
              })}
            </svg>
          </div>
        </div>
      </div>

      <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">
        Turn Radio Live to hear AI dispatch voice traffic with static filters — incidents and your blueprint's Radio Broadcast nodes are read aloud as they fire. Unit status updates automatically: dispatched → on scene → clear.
      </p>
    </div>
  );
}
